import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Tabs,
  Tab,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  alpha,
  useTheme
} from '@mui/material';
import { 
  CalendarMonth as CalendarIcon,
  Event as EventIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  ViewDay as DayViewIcon,
  ViewWeek as WeekViewIcon,
  ViewModule as MonthViewIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  AccessTime as TimeIcon,
  Check as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { useEvents } from '../contexts/EventContext';
import { styled, keyframes } from '@mui/material/styles';

// Tipos
interface EventData {
  id: number;
  title: string;
  date: Date;
  time: string;
  client: string;
  clientId?: number;
  type: 'visita' | 'contrato' | 'avaliacao' | 'reuniao' | 'outro';
  status: 'agendado' | 'confirmado' | 'concluido' | 'cancelado';
  location?: string;
  description?: string;
  propertyId?: number;
  responsibleId?: number;
  createdAt: Date;
  updatedAt: Date;
  notes?: string;
  isNew?: boolean;
}

const EventsPage: React.FC = () => {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState<'day' | 'week' | 'month' | 'list'>('list');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<EventData | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formDate, setFormDate] = useState<Date | null>(new Date());
  const [formTime, setFormTime] = useState<Date | null>(new Date());
  const [formClient, setFormClient] = useState('');
  const [formType, setFormType] = useState<EventData['type']>('visita');
  const [formStatus, setFormStatus] = useState<EventData['status']>('agendado');
  const [formLocation, setFormLocation] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPropertyId, setFormPropertyId] = useState<number | undefined>(undefined);
  const [formResponsibleId, setFormResponsibleId] = useState<number | undefined>(undefined);
  const [formNotes, setFormNotes] = useState('');

  // Usando o contexto de eventos
  const { 
    events, 
    addEvent, 
    updateEvent, 
    deleteEvent,
    markAllEventsAsRead 
  } = useEvents();

  // Seletores Redux para integração com outros dados do sistema
  const leads = useSelector((state: RootState) => state.leads?.items || []);
  const products = useSelector((state: RootState) => state.products?.items || []);

  // Filtragem de eventos
  const filteredEvents = events.filter(event => {
    // Filtro por termo de busca
    if (searchTerm && !event.title.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !event.client.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !(event.description?.toLowerCase().includes(searchTerm.toLowerCase()))) {
      return false;
    }
    
    // Filtro por status
    if (filterStatus !== 'all' && event.status !== filterStatus) {
      return false;
    }
    
    // Filtro por tipo
    if (filterType !== 'all' && event.type !== filterType) {
      return false;
    }
    
    // Filtro por data selecionada (apenas para visualizações de dia)
    if (viewMode === 'day') {
      const eventDate = new Date(event.date);
      return (
        eventDate.getDate() === selectedDate.getDate() &&
        eventDate.getMonth() === selectedDate.getMonth() &&
        eventDate.getFullYear() === selectedDate.getFullYear()
      );
    }
    
    return true;
  }).sort((a, b) => {
    // Ordenar por data e hora
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    
    if (dateA.getTime() !== dateB.getTime()) {
      return dateA.getTime() - dateB.getTime();
    }
    
    // Se as datas forem iguais, ordenar por hora
    const timeA = a.time.split(':').map(Number);
    const timeB = b.time.split(':').map(Number);
    return (timeA[0] * 60 + timeA[1]) - (timeB[0] * 60 + timeB[1]);
  });

  // Funções auxiliares
  const getEventTypeColor = (type: string) => {
    switch (type) {
      case 'visita': return theme.palette.primary.main;
      case 'contrato': return theme.palette.success.main;
      case 'avaliacao': return theme.palette.info.main;
      case 'reuniao': return theme.palette.warning.main;
      default: return theme.palette.grey[500];
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'agendado': return theme.palette.info.main;
      case 'confirmado': return theme.palette.warning.main;
      case 'concluido': return theme.palette.success.main;
      case 'cancelado': return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'agendado': return 'Agendado';
      case 'confirmado': return 'Confirmado';
      case 'concluido': return 'Concluído';
      case 'cancelado': return 'Cancelado';
      default: return status;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'visita': return 'Visita';
      case 'contrato': return 'Contrato';
      case 'avaliacao': return 'Avaliação';
      case 'reuniao': return 'Reunião';
      default: return 'Outro';
    }
  };

  const formatDate = (date: Date) => {
    return format(date, 'dd/MM/yyyy', { locale: ptBR });
  };

  // Handlers
  const handleAddEvent = () => {
    setCurrentEvent(null);
    
    // Resetar o formulário
    setFormTitle('');
    setFormDate(new Date());
    setFormTime(new Date());
    setFormClient('');
    setFormType('visita');
    setFormStatus('agendado');
    setFormLocation('');
    setFormDescription('');
    setFormPropertyId(undefined);
    setFormResponsibleId(undefined);
    setFormNotes('');
    
    setTabValue(0);
    setOpenDialog(true);
  };

  const handleEditEvent = (event: EventData) => {
    setCurrentEvent(event);
    
    // Preencher o formulário com os dados do evento
    setFormTitle(event.title);
    setFormDate(new Date(event.date));
    
    // Converter a string de tempo para um objeto Date
    const [hours, minutes] = event.time.split(':').map(Number);
    const timeDate = new Date();
    timeDate.setHours(hours, minutes, 0);
    setFormTime(timeDate);
    
    setFormClient(event.client);
    setFormType(event.type);
    setFormStatus(event.status);
    setFormLocation(event.location || '');
    setFormDescription(event.description || '');
    setFormPropertyId(event.propertyId);
    setFormResponsibleId(event.responsibleId);
    setFormNotes(event.notes || '');
    
    setTabValue(0);
    setOpenDialog(true);
  };

  const handleDeleteEvent = (id: number) => {
    deleteEvent(id);
  };

  const handleSaveEvent = () => {
    if (!formTitle || !formDate || !formTime || !formClient) {
      // Validação básica
      return;
    }
    
    // Formatar a hora para o formato HH:MM
    const hours = formTime?.getHours().toString().padStart(2, '0') || '00';
    const minutes = formTime?.getMinutes().toString().padStart(2, '0') || '00';
    const timeString = `${hours}:${minutes}`;
    
    const eventData: Partial<EventData> = {
      title: formTitle,
      date: formDate || new Date(),
      time: timeString,
      client: formClient,
      type: formType,
      status: formStatus,
      location: formLocation || undefined,
      description: formDescription || undefined,
      propertyId: formPropertyId,
      responsibleId: formResponsibleId,
      notes: formNotes || undefined,
    };
    
    if (currentEvent) {
      // Atualizar evento existente
      updateEvent({
        ...currentEvent,
        ...eventData,
      });
    } else {
      // Adicionar novo evento
      addEvent(eventData as any);
    }
    
    setOpenDialog(false);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleViewModeChange = (mode: 'day' | 'week' | 'month' | 'list') => {
    setViewMode(mode);
  };

  // Marcar todos os eventos como lidos ao montar o componente
  React.useEffect(() => {
    markAllEventsAsRead();
  }, [markAllEventsAsRead]);

  // Renderização da lista de eventos
  const renderEventsList = () => {
    return (
      <Stack spacing={2} sx={{ mt: 2 }}>
        {filteredEvents.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Nenhum evento encontrado com os filtros atuais.
            </Typography>
          </Paper>
        ) : (
          filteredEvents.map(event => (
            <Paper
              key={event.id}
              sx={{
                p: 2,
                bgcolor: alpha(getEventTypeColor(event.type), 0.05),
                borderLeft: `4px solid ${getEventTypeColor(event.type)}`,
                transition: 'transform 0.2s ease-in-out',
                position: 'relative',
                ...(event.isNew && {
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.error.main,
                    animation: `${keyframes`
                      0% { opacity: 0.5; }
                      50% { opacity: 1; }
                      100% { opacity: 0.5; }
                    `} 1.5s infinite ease-in-out`
                  }
                }),
                '&:hover': {
                  transform: 'translateX(5px)',
                  bgcolor: alpha(getEventTypeColor(event.type), 0.1),
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {event.title}
                    {event.isNew && (
                      <Chip 
                        label="Novo" 
                        size="small" 
                        color="error" 
                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} 
                      />
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <CalendarIcon fontSize="small" /> {formatDate(new Date(event.date))} às {event.time}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <PersonIcon fontSize="small" /> {event.client}
                  </Typography>
                  {event.location && (
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <HomeIcon fontSize="small" /> {event.location}
                    </Typography>
                  )}
                  {event.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {event.description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip 
                      label={getTypeLabel(event.type)} 
                      size="small" 
                      sx={{ bgcolor: alpha(getEventTypeColor(event.type), 0.1), color: getEventTypeColor(event.type) }} 
                    />
                    <Chip 
                      label={getStatusLabel(event.status)} 
                      size="small" 
                      sx={{ bgcolor: alpha(getStatusColor(event.status), 0.1), color: getStatusColor(event.status) }} 
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton size="small" onClick={() => handleEditEvent(event)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteEvent(event.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Paper>
          ))
        )}
      </Stack>
    );
  };

  // Renderização do calendário (simplificado)
  const renderCalendarView = () => {
    return (
      <Paper sx={{ p: 3, mt: 2, minHeight: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Visualização de calendário {viewMode} para {formatDate(selectedDate)}
          <br />
          (Interface completa de calendário seria implementada aqui)
        </Typography>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EventIcon fontSize="large" color="primary" /> Gerenciamento de Eventos
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ boxShadow: theme.shadows[8] }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button 
                    variant={viewMode === 'day' ? 'contained' : 'outlined'} 
                    startIcon={<DayViewIcon />}
                    onClick={() => handleViewModeChange('day')}
                    size="small"
                  >
                    Dia
                  </Button>
                  <Button 
                    variant={viewMode === 'week' ? 'contained' : 'outlined'} 
                    startIcon={<WeekViewIcon />}
                    onClick={() => handleViewModeChange('week')}
                    size="small"
                  >
                    Semana
                  </Button>
                  <Button 
                    variant={viewMode === 'month' ? 'contained' : 'outlined'} 
                    startIcon={<MonthViewIcon />}
                    onClick={() => handleViewModeChange('month')}
                    size="small"
                  >
                    Mês
                  </Button>
                  <Button 
                    variant={viewMode === 'list' ? 'contained' : 'outlined'} 
                    startIcon={<FilterIcon />}
                    onClick={() => handleViewModeChange('list')}
                    size="small"
                  >
                    Lista
                  </Button>
                </Box>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<AddIcon />}
                  onClick={handleAddEvent}
                >
                  Novo Evento
                </Button>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="Buscar eventos..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filterStatus}
                      label="Status"
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <MenuItem value="all">Todos os status</MenuItem>
                      <MenuItem value="agendado">Agendado</MenuItem>
                      <MenuItem value="confirmado">Confirmado</MenuItem>
                      <MenuItem value="concluido">Concluído</MenuItem>
                      <MenuItem value="cancelado">Cancelado</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      value={filterType}
                      label="Tipo"
                      onChange={(e) => setFilterType(e.target.value)}
                    >
                      <MenuItem value="all">Todos os tipos</MenuItem>
                      <MenuItem value="visita">Visita</MenuItem>
                      <MenuItem value="contrato">Contrato</MenuItem>
                      <MenuItem value="avaliacao">Avaliação</MenuItem>
                      <MenuItem value="reuniao">Reunião</MenuItem>
                      <MenuItem value="outro">Outro</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Data"
                      value={selectedDate}
                      onChange={(newValue) => newValue && setSelectedDate(newValue)}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                {viewMode === 'list' ? renderEventsList() : renderCalendarView()}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog para adicionar/editar evento */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentEvent ? 'Editar Evento' : 'Novo Evento'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Informações Básicas" />
              <Tab label="Detalhes" />
              <Tab label="Notas" />
            </Tabs>
          </Box>

          {tabValue === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Título"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                  <DatePicker
                    label="Data"
                    value={formDate}
                    onChange={(newValue) => setFormDate(newValue)}
                    slotProps={{ textField: { fullWidth: true, required: true } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                  <TimePicker
                    label="Horário"
                    value={formTime}
                    onChange={(newValue) => setFormTime(newValue)}
                    slotProps={{ textField: { fullWidth: true, required: true } }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={formType}
                    label="Tipo"
                    onChange={(e) => setFormType(e.target.value as EventData['type'])}
                  >
                    <MenuItem value="visita">Visita</MenuItem>
                    <MenuItem value="contrato">Contrato</MenuItem>
                    <MenuItem value="avaliacao">Avaliação</MenuItem>
                    <MenuItem value="reuniao">Reunião</MenuItem>
                    <MenuItem value="outro">Outro</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formStatus}
                    label="Status"
                    onChange={(e) => setFormStatus(e.target.value as EventData['status'])}
                  >
                    <MenuItem value="agendado">Agendado</MenuItem>
                    <MenuItem value="confirmado">Confirmado</MenuItem>
                    <MenuItem value="concluido">Concluído</MenuItem>
                    <MenuItem value="cancelado">Cancelado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Cliente"
                  value={formClient}
                  onChange={(e) => setFormClient(e.target.value)}
                  required
                />
              </Grid>
            </Grid>
          )}

          {tabValue === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Localização"
                  value={formLocation}
                  onChange={(e) => setFormLocation(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descrição"
                  multiline
                  rows={4}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Imóvel Relacionado</InputLabel>
                  <Select
                    value={formPropertyId || ''}
                    label="Imóvel Relacionado"
                    onChange={(e) => setFormPropertyId(e.target.value ? Number(e.target.value) : undefined)}
                  >
                    <MenuItem value="">Nenhum</MenuItem>
                    {products.map((product: any) => (
                      <MenuItem key={product.id} value={product.id}>
                        {product.name || `Imóvel #${product.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Responsável</InputLabel>
                  <Select
                    value={formResponsibleId || ''}
                    label="Responsável"
                    onChange={(e) => setFormResponsibleId(e.target.value ? Number(e.target.value) : undefined)}
                  >
                    <MenuItem value="">Selecione...</MenuItem>
                    <MenuItem value="301">Carlos Silva</MenuItem>
                    <MenuItem value="302">Ana Oliveira</MenuItem>
                    <MenuItem value="303">Marcos Santos</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}

          {tabValue === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notas"
                  multiline
                  rows={8}
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  placeholder="Adicione notas e observações sobre este evento..."
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSaveEvent}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventsPage;
